/**
 * Math Renderer - LaTeX with KaTeX (lazy loaded)
 */

import { useEffect, useRef, useState } from 'react';

interface Props {
  content: string;
  inline?: boolean;
  className?: string;
}

export default function MathRenderer({ content, inline = false, className = '' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function renderMath() {
      if (!containerRef.current) return;

      try {
        // Lazy load KaTeX
        const katex = await import('katex');
        await import('katex/dist/katex.min.css');

        if (!mounted) return;

        katex.default.render(content, containerRef.current, {
          displayMode: !inline,
          throwOnError: false,
          errorColor: '#cc0000',
          strict: false,
          trust: false,
        });
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to render math');
        }
      }
    }

    renderMath();

    return () => {
      mounted = false;
    };
  }, [content, inline]);

  if (error) {
    return <span className='text-red-600 text-sm font-mono'>[Math Error: {error}]</span>;
  }

  return (
    <span
      ref={containerRef}
      className={`math-renderer ${inline ? 'inline-math' : 'display-math'} ${className}`}
    />
  );
}

/**
 * Parse text with LaTeX delimiters and render
 */
export function MathText({ text, className = '' }: { text: string; className?: string }) {
  // Identify common math types: $$...$$, $...$, \[...\], \(...\)
  const parts = text.split(
    /(\$\$[\s\S]+?\$\$|\$[\s\S]+?\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\))/g
  );

  return (
    <span className={className}>
      {parts.map((part, idx) => {
        if (!part) return null;

        // Type block $$...$$
        if (part.startsWith('$$') && part.endsWith('$$')) {
          return <MathRenderer key={idx} content={part.slice(2, -2).trim()} inline={false} />;
        }

        // Type inline $...$
        if (part.startsWith('$') && part.endsWith('$')) {
          return <MathRenderer key={idx} content={part.slice(1, -1).trim()} inline={true} />;
        }

        // Type block \[...\]
        if (part.startsWith('\\[') && part.endsWith('\\]')) {
          return <MathRenderer key={idx} content={part.slice(2, -2).trim()} inline={false} />;
        }

        // Type inline \(...\)
        if (part.startsWith('\\(') && part.endsWith('\\)')) {
          return <MathRenderer key={idx} content={part.slice(2, -2).trim()} inline={true} />;
        }

        // Text normal
        return <span key={idx}>{part}</span>;
      })}
    </span>
  );
}



